import type { Json } from '@/types/database';

/**
 * Payment rails a card owner can accept orders through.
 *
 * Peer-to-peer rails (Venmo, Cash App, PayPal, Square link, Zelle) have no
 * merchant API we can poll, so the flow is: visitor places order → we hand
 * them a pre-filled pay link (or Zelle instructions) with the order number
 * in the note → visitor taps "I've sent it" → owner confirms in /app/orders.
 *
 * Stripe is the exception: the visitor is sent to a hosted Stripe Checkout
 * on the owner's connected Express account and the webhook marks the order
 * paid on its own.
 *
 * Stored on `cards.payment_config` (jsonb, public-readable — visitors need
 * it to pay). Keep it small: the DB caps the column at 4 KB.
 */

export type PaymentMethod = 'stripe' | 'venmo' | 'cashapp' | 'paypal' | 'zelle' | 'square' | 'cash';

/** Display / editor order. */
export const PAYMENT_METHODS: PaymentMethod[] = ['stripe', 'venmo', 'cashapp', 'paypal', 'zelle', 'square', 'cash'];

export interface PaymentConfig {
  currency?: string;
  /**
   * Master switch for the shop. Off by default: products, the cart bar and
   * checkout stay hidden from visitors until the owner turns it on, so a
   * half-built shop never shows.
   */
  shop_enabled?: boolean;
  /** Set by the editor once the owner's Stripe Express account can take charges. */
  stripe?: { enabled: boolean };
  venmo?: { enabled: boolean; handle: string };
  /** Cash App $cashtag — https://cash.app/$tag/<amount> */
  cashapp?: { enabled: boolean; cashtag: string };
  /** paypal.me handle — https://paypal.me/<handle> */
  paypal?: { enabled: boolean; handle: string };
  /** A Square checkout / payment link. */
  square?: { enabled: boolean; url: string };
  /** Zelle has no pay-link; we show the enrolled email/phone + name. */
  zelle?: { enabled: boolean; contact: string; name?: string };
  /**
   * Cash on delivery / pay at pickup. Nothing is paid online: the order
   * waits at payment_status 'pending' until the owner collects the cash and
   * marks it paid. `note` is shown to the visitor (where / when to pay).
   */
  cash?: { enabled: boolean; note?: string };
  /** Optional note shown on the checkout sheet (pickup info, lead times…). */
  instructions?: string;
}

export interface PaymentMethodMeta {
  id: PaymentMethod;
  name: string;
  /** Brand color used for the method pill. */
  color: string;
  /** Short helper for the editor. */
  fieldLabel: string;
  placeholder: string;
  hint: string;
  /** True when the payment confirms itself (no owner confirmation needed). */
  automatic?: boolean;
  /** Shorter label for tight spaces (the checkout method grid). */
  shortName?: string;
}

export const PAYMENT_META: Record<PaymentMethod, PaymentMethodMeta> = {
  stripe: {
    id: 'stripe',
    name: 'Card',
    color: '#635BFF',
    fieldLabel: 'Stripe account',
    placeholder: '',
    hint: 'Cards, Apple Pay, Google Pay via Stripe. Confirms itself — Stripe fees apply.',
    automatic: true,
  },
  venmo: {
    id: 'venmo',
    name: 'Venmo',
    color: '#008CFF',
    fieldLabel: 'Venmo username',
    placeholder: '@your-handle',
    hint: 'Visitors get a pre-filled Venmo payment with the amount and order number.',
  },
  cashapp: {
    id: 'cashapp',
    name: 'Cash App',
    color: '#00D632',
    fieldLabel: 'Cash App $cashtag',
    placeholder: '$yourname',
    hint: 'Visitors get a cash.app link pre-filled with the amount.',
  },
  paypal: {
    id: 'paypal',
    name: 'PayPal',
    color: '#003087',
    fieldLabel: 'PayPal.Me handle',
    placeholder: 'yourname',
    hint: 'Create yours at paypal.me. We link to paypal.me/<handle>/<amount>.',
  },
  zelle: {
    id: 'zelle',
    name: 'Zelle',
    color: '#6D1ED4',
    fieldLabel: 'Zelle email or phone',
    placeholder: 'you@bank.com  or  +1 555 000 0000',
    hint: 'Zelle has no pay link — we show the visitor your contact and the amount to send.',
  },
  square: {
    id: 'square',
    name: 'Square',
    color: '#111111',
    fieldLabel: 'Square payment link',
    placeholder: 'https://square.link/u/…',
    hint: 'Paste a Square checkout link (Dashboard → Payment links).',
  },
  cash: {
    id: 'cash',
    name: 'Cash on delivery',
    shortName: 'Cash',
    color: '#1F7A4D',
    fieldLabel: 'Note for customers (optional)',
    placeholder: 'Pay when you pick up at the market, or on delivery. Exact change appreciated.',
    hint: 'Customers pay in cash when you hand the order over. Nothing to set up — you mark it paid.',
  },
};

/** Parse whatever is in cards.payment_config into a typed, defensive shape. */
export function parsePaymentConfig(raw: Json | null | undefined): PaymentConfig {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const r = raw as Record<string, unknown>;
  const cfg: PaymentConfig = {};
  if (typeof r.currency === 'string') cfg.currency = r.currency;
  if (typeof r.shop_enabled === 'boolean') cfg.shop_enabled = r.shop_enabled;
  if (typeof r.instructions === 'string') cfg.instructions = r.instructions;
  const stripe = obj(r.stripe);
  if (stripe) cfg.stripe = { enabled: !!stripe.enabled };
  const venmo = obj(r.venmo);
  if (venmo) cfg.venmo = { enabled: !!venmo.enabled, handle: str(venmo.handle) };
  const cashapp = obj(r.cashapp);
  if (cashapp) cfg.cashapp = { enabled: !!cashapp.enabled, cashtag: str(cashapp.cashtag) };
  const paypal = obj(r.paypal);
  if (paypal) cfg.paypal = { enabled: !!paypal.enabled, handle: str(paypal.handle) };
  const square = obj(r.square);
  if (square) cfg.square = { enabled: !!square.enabled, url: str(square.url) };
  const cash = obj(r.cash);
  if (cash) cfg.cash = { enabled: !!cash.enabled, note: typeof cash.note === 'string' ? cash.note : undefined };
  const zelle = obj(r.zelle);
  if (zelle) {
    cfg.zelle = {
      enabled: !!zelle.enabled,
      contact: str(zelle.contact),
      name: typeof zelle.name === 'string' ? zelle.name : undefined,
    };
  }
  return cfg;
}

function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}
function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** The shop master switch — off unless explicitly turned on. */
export function shopEnabled(cfg: PaymentConfig): boolean {
  return cfg.shop_enabled === true;
}

/** Methods that are switched on AND have the field they need filled in. */
export function enabledPaymentMethods(cfg: PaymentConfig): PaymentMethod[] {
  const out: PaymentMethod[] = [];
  if (cfg.stripe?.enabled) out.push('stripe');
  if (cfg.venmo?.enabled && cleanHandle(cfg.venmo.handle)) out.push('venmo');
  if (cfg.cashapp?.enabled && cleanCashtag(cfg.cashapp.cashtag)) out.push('cashapp');
  if (cfg.paypal?.enabled && cleanHandle(cfg.paypal.handle)) out.push('paypal');
  if (cfg.zelle?.enabled && cfg.zelle.contact.trim()) out.push('zelle');
  if (cfg.square?.enabled && isHttpsUrl(cfg.square.url)) out.push('square');
  if (cfg.cash?.enabled) out.push('cash');
  return out;
}

export function cleanHandle(h: string): string {
  return h
    .trim()
    .replace(/^@/, '')
    .replace(/^https?:\/\/(www\.)?(venmo\.com|paypal\.me|account\.venmo\.com\/u)\//i, '')
    .replace(/\/.*$/, '');
}

/** "$roger" / "roger" / "https://cash.app/$roger" → "roger" */
export function cleanCashtag(t: string): string {
  return t
    .trim()
    .replace(/^https?:\/\/(www\.)?cash\.app\//i, '')
    .replace(/^\$/, '')
    .replace(/\/.*$/, '')
    .replace(/[^a-zA-Z0-9_-]/g, '');
}

export function isHttpsUrl(u: string): boolean {
  try {
    const p = new URL(u.trim());
    return p.protocol === 'https:';
  } catch {
    return false;
  }
}

export interface PayTarget {
  /** Tap target. Null for Zelle (instructions only), Stripe (server redirect) and cash (paid on handover). */
  href: string | null;
  /** Secondary web link (e.g. Venmo web when the app deep-link isn't handled). */
  webHref?: string | null;
  /** Human-readable destination the visitor can copy (handle, email, phone). */
  display: string;
  label: string;
}

/**
 * Build the pay link for a method. `note` should carry the order number so
 * the owner can match the payment to the order.
 */
export function payTarget(
  method: PaymentMethod,
  cfg: PaymentConfig,
  amountCents: number,
  note: string,
): PayTarget {
  const amount = (amountCents / 100).toFixed(2);
  switch (method) {
    case 'stripe':
      return { href: null, display: 'Stripe Checkout', label: 'Pay by card' };
    case 'venmo': {
      const handle = cleanHandle(cfg.venmo?.handle ?? '');
      const qs = `txn=pay&audience=private&recipients=${encodeURIComponent(handle)}&amount=${amount}&note=${encodeURIComponent(note)}`;
      return {
        href: `https://venmo.com/${encodeURIComponent(handle)}?${qs}`,
        webHref: `https://account.venmo.com/u/${encodeURIComponent(handle)}`,
        display: `@${handle}`,
        label: 'Pay with Venmo',
      };
    }
    case 'cashapp': {
      const tag = cleanCashtag(cfg.cashapp?.cashtag ?? '');
      return {
        href: `https://cash.app/$${encodeURIComponent(tag)}/${amount}`,
        display: `$${tag}`,
        label: 'Pay with Cash App',
      };
    }
    case 'paypal': {
      const handle = cleanHandle(cfg.paypal?.handle ?? '');
      const currency = (cfg.currency ?? 'USD').toUpperCase();
      return {
        href: `https://paypal.me/${encodeURIComponent(handle)}/${amount}${currency}`,
        display: `paypal.me/${handle}`,
        label: 'Pay with PayPal',
      };
    }
    case 'square': {
      const url = (cfg.square?.url ?? '').trim();
      let display = url;
      try {
        display = new URL(url).host + new URL(url).pathname;
      } catch {
        /* keep raw */
      }
      return { href: url, display, label: 'Pay with Square' };
    }
    case 'zelle':
      return {
        href: null,
        display: (cfg.zelle?.contact ?? '').trim(),
        label: 'Send with Zelle',
      };
    case 'cash':
      return { href: null, display: 'Cash on delivery', label: 'Pay in cash on delivery' };
  }
}
